<script setup lang="ts">
import type { CSSProperties } from "vue";
import type { GameVehicleSummary, WikiVehicle } from "~~/types/gamedata";

const props = defineProps({
  vehicle: {
    type: Object as () => GameVehicleSummary,
    required: true,
  },
  raw: {
    type: Object as () => WikiVehicle,
    required: true,
  },
});

const { data: image, pending } = useFleetyardsShipImage(() => props.vehicle);

const dimensions = computed(() => props.vehicle.dimensions);
const mass = computed(() => {
  const total = Number(props.raw.mass_total);
  return Number.isFinite(total) && total > 0 ? total : null;
});

const dimensionStyle = computed(() => {
  const length = Number(dimensions.value.length) || 0;
  const width = Number(dimensions.value.width) || 0;
  const height = Number(dimensions.value.height) || 0;

  return {
    "--dimension-length": length,
    "--dimension-width": width,
    "--dimension-height": height,
    "--dimension-longest": Math.max(length, width, height, 1.8),
  } as CSSProperties;
});

const tabs = computed<ShipexkursTab[]>(() => [{
  label: 'Spezifikationen',
  icon: 'i-lucide-gauge',
  items: [{
    label: 'Länge',
    value: String(props.vehicle.dimensions.length),
    suffix: 'METER',
    third: true,
  },
  {
    label: 'Breite',
    value: String(props.vehicle.dimensions.width),
    suffix: 'METER',
    third: true,
  },
  {
    label: 'Höhe',
    value: String(props.vehicle.dimensions.height),
    suffix: 'METER',
    third: true,
  }, {
    label: 'Leergewicht',
    value: String(formatCurrency(Number(props.raw.mass_hull))),
    suffix: 'KG',
    third: true,
  }, {
    label: 'Gewicht (Komponenten & Waffen)',
    value: String(formatCurrency(Number(props.raw.mass_loadout))),
    suffix: 'KG',
    third: true,
  }, {
    label: 'Gesamtgewicht',
    value: String(formatCurrency(Number(props.raw.mass_total))),
    suffix: 'KG',
    third: true,
  }],
}]);
</script>

<template>
  <UCard variant="outline" class="dimensions-card bg-bunker-950 ring-0 border border-default" :ui="{ body: 'pt-2 sm:pt-2 pb-0 sm:pb-0 sm:px-0 px-0' }">
    <div class="flex items-center space-x-2 border-b border-default pt-0 pb-2">
      <UIcon name="i-lucide-ruler" class="size-5 ml-3" />
      <span class="text-sm text-primary font-medium">Abmessungen</span>
    </div>

    <div class="dimensions-stage" :style="dimensionStyle">
      <span class="sr-only">
        Abmessungen: Länge {{ dimensions.length }} m, Breite {{ dimensions.width }} m,
        Höhe {{ dimensions.height }} m<span v-if="mass">, Masse {{ formatCurrency(mass) }} kg</span>.
      </span>

      <div class="dimensions-visual" aria-hidden="true">
        <div class="dimensions-ship">
          <img
            v-if="image"
            :src="image"
            :alt="`${vehicle.name} – Ansicht schräg von vorne`"
            loading="lazy"
            class="absolute inset-0 size-[75%] mt-[28%] ml-[5%] object-contain"
          >
          <div v-else-if="pending" class="absolute inset-0 flex items-center justify-center">
            <UIcon name="i-lucide-loader-2" class="size-6 animate-spin text-neutral-500" />
          </div>
          <ShipexkursShipImage
            v-else
            :vehicle="vehicle"
            img-class="absolute inset-0 size-full object-contain"
          />
        </div>

        <div class="dimensions-scene-position">
          <div class="dimensions-scene">
            <div class="dimensions-layer dimensions-layer-top">
              <div v-if="dimensions.height != null" class="dimensions-line dimensions-line-height" />
              <div v-if="dimensions.height != null" class="dimensions-height-anchor">
                <div class="dimensions-text dimensions-text-height">
                  <span class="dimensions-label">Höhe</span>
                  <span class="dimensions-value">{{ dimensions.height }} m</span>
                </div>
              </div>
            </div>

            <div class="dimensions-layer dimensions-layer-bottom">
              <div class="dimensions-reference">
                <div class="dimensions-reference-layer">
                  <span class="dimensions-reference-top" />
                  <span class="dimensions-reference-front" />
                  <span class="dimensions-reference-right" />
                </div>
              </div>

              <template v-if="dimensions.length != null">
                <div class="dimensions-line dimensions-line-length" />
                <div class="dimensions-text dimensions-text-length">
                  <span class="dimensions-label">Länge</span>
                  <span class="dimensions-value">{{ dimensions.length }} m</span>
                </div>
              </template>

              <template v-if="dimensions.width != null">
                <div class="dimensions-line dimensions-line-width" />
                <div class="dimensions-text dimensions-text-width">
                  <span class="dimensions-label">Breite</span>
                  <span class="dimensions-value">{{ dimensions.width }} m</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between gap-3 border-b border-default px-4 py-2 text-xs text-muted">
      <!-- <div v-if="mass" class="flex items-baseline gap-1.5 whitespace-nowrap"> -->
        <!-- <span>Masse</span> -->
        <!-- <span class="text-sm font-semibold text-highlighted">{{ formatCurrency(mass) }} kg</span> -->
      <!-- </div> -->
      <div class="ml-auto flex items-center gap-1.5 whitespace-nowrap">
        <span class="size-2.5 rounded-sm bg-sky-500/80" />
        <span>Mensch · 1,8 m</span>
      </div>
    </div>
    <ShipexkursShipTable :tabs="tabs" :hide-header="true" />
  </UCard>
</template>

<style scoped>
.dimensions-card {
  container-type: inline-size;
}

.dimensions-stage {
  --dimension-container-size: clamp(165px, 42cqi, 210px);
  --dimension-scale: calc(var(--dimension-container-size) / var(--dimension-longest));
  --dimension-box-length: calc(var(--dimension-length) * var(--dimension-scale));
  --dimension-box-width: calc(var(--dimension-width) * var(--dimension-scale));
  --dimension-box-height: calc(var(--dimension-height) * var(--dimension-scale));
  --dimension-reference-length: calc(0.3 * var(--dimension-scale));
  --dimension-reference-width: calc(0.5 * var(--dimension-scale));
  --dimension-reference-height: calc(1.8 * var(--dimension-scale));
  --dimension-reference-clear: calc(var(--dimension-reference-length) + 12px);
  --dimension-offset-up: calc(var(--dimension-box-height) / 2);
  --dimension-offset-down: calc(var(--dimension-box-height) / -2);
  --dimension-scene-transform: rotateX(45deg) rotateZ(-45deg);
  --dimension-inverse-transform: rotateZ(45deg) rotateX(-45deg);
  --dimension-line-color: var(--ui-border-accented);
  --dimension-stage-height: calc(
    var(--dimension-box-height) / 2
    + hypot(var(--dimension-box-length), var(--dimension-box-width))
    + 80px
  );

  position: relative;
  display: grid;
  height: var(--dimension-stage-height);
  min-height: 285px;
  place-content: center;
  overflow: hidden;
}

.dimensions-visual {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  transform: translateY(-16px) scale(1.12);
  transform-origin: center;
}

@container (min-width: 400px) {
  .dimensions-visual {
    transform: translateY(-36px) scale(1.12);
  }
}

.dimensions-ship {
  position: absolute;
  z-index: 2;
  top: 45%;
  left: 50%;
  width: calc(var(--dimension-container-size) * 1.4);
  aspect-ratio: 1.38;
  transform: translate(-47%, -64%);
}

.dimensions-scene-position {
  z-index: 1;
  display: grid;
  transform: translateY(18px);
}

.dimensions-scene,
.dimensions-layer,
.dimensions-reference,
.dimensions-reference-layer {
  transform-style: preserve-3d;
}

.dimensions-scene {
  display: grid;
  width: var(--dimension-box-length);
  height: var(--dimension-box-width);
  transform: var(--dimension-scene-transform);
}

.dimensions-layer {
  position: relative;
  grid-area: 1 / 1;
  width: var(--dimension-box-length);
  height: var(--dimension-box-width);
}

.dimensions-layer-top {
  transform: translateZ(var(--dimension-offset-up));
}

.dimensions-layer-bottom {
  transform: translateZ(var(--dimension-offset-down));
}

.dimensions-line {
  position: absolute;
}

.dimensions-line::before,
.dimensions-line::after {
  position: absolute;
  content: "";
  background: var(--dimension-line-color);
}

.dimensions-line-length {
  top: calc(100% + 10px);
  left: 0;
  width: 100%;
  height: 0;
  border-top: 1px solid var(--dimension-line-color);
}

.dimensions-line-length::before,
.dimensions-line-length::after {
  top: -5px;
  width: 1px;
  height: 9px;
}

.dimensions-line-length::before {
  left: 0;
}

.dimensions-line-length::after {
  right: 0;
}

.dimensions-line-width {
  top: 0;
  left: calc(-10px - var(--dimension-reference-clear));
  width: 0;
  height: 100%;
  border-left: 1px solid var(--dimension-line-color);
}

.dimensions-line-width::before,
.dimensions-line-width::after {
  left: -5px;
  width: 9px;
  height: 1px;
}

.dimensions-line-width::before {
  top: 0;
}

.dimensions-line-width::after {
  bottom: 0;
}

.dimensions-line-height {
  bottom: var(--dimension-offset-down);
  left: 0;
  width: calc(var(--dimension-box-length) + 10px);
  height: var(--dimension-box-height);
  border-right: 1px solid var(--dimension-line-color);
  transform: rotateX(90deg) translateY(var(--dimension-offset-down));
}

.dimensions-line-height::before,
.dimensions-line-height::after {
  right: -5px;
  width: 9px;
  height: 1px;
}

.dimensions-line-height::before {
  top: 0;
}

.dimensions-line-height::after {
  bottom: 0;
}

.dimensions-text {
  position: absolute;
  display: flex;
  width: max-content;
  flex-direction: column;
  white-space: nowrap;
}

.dimensions-label {
  color: var(--ui-text-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
}

.dimensions-value {
  color: var(--ui-text-highlighted);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}

.dimensions-text-length {
  top: calc(100% + 14px);
  left: 50%;
  text-align: center;
  transform: translateX(-50%);
}

.dimensions-text-width {
  top: 50%;
  left: calc(-16px - var(--dimension-reference-clear));
  text-align: center;
  transform: rotate(90deg) translateX(-50%);
  transform-origin: top left;
}

.dimensions-height-anchor {
  position: absolute;
  right: 0;
  bottom: 0;
  transform: var(--dimension-inverse-transform);
  transform-style: preserve-3d;
}

.dimensions-text-height {
  top: calc(var(--dimension-box-height) * 0.35355);
  left: 20px;
  transform: translateY(-50%);
}

.dimensions-reference {
  position: absolute;
  top: calc(100% - var(--dimension-reference-width));
  left: calc(-1 * var(--dimension-reference-length) - 10px);
  width: var(--dimension-reference-length);
  height: var(--dimension-reference-width);
  transform: translateZ(calc(var(--dimension-reference-height) / 2));
}

.dimensions-reference-layer {
  position: absolute;
  inset: 0;
  transform: translateZ(calc(var(--dimension-reference-height) / 2));
}

.dimensions-reference-top,
.dimensions-reference-front,
.dimensions-reference-right {
  position: absolute;
  box-shadow: 0 0 0 1px rgb(56 189 248 / 75%);
}

.dimensions-reference-top {
  inset: 0;
  background: rgb(125 211 252 / 85%);
}

.dimensions-reference-front {
  top: 0;
  left: calc(var(--dimension-reference-height) / -2);
  width: var(--dimension-reference-height);
  height: var(--dimension-reference-width);
  background: rgb(3 105 161 / 85%);
  transform: rotateY(90deg) translateX(calc(var(--dimension-reference-height) / 2));
}

.dimensions-reference-right {
  bottom: calc(var(--dimension-reference-height) / -2);
  left: 0;
  width: var(--dimension-reference-length);
  height: var(--dimension-reference-height);
  background: rgb(14 165 233 / 85%);
  transform: rotateX(90deg) translateY(calc(var(--dimension-reference-height) / -2));
}
</style>
